import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const LAST_UPDATED = "18 October 2026";
const OPERATOR = "AIORBIT APPS (OPC) PRIVATE LIMITED";
const PRIVACY_EMAIL = "admin@aiorbitapps.com";
const BUSINESS_ADDRESS =
  "67-21-17 Lalbahadur Nagar, Kakinada, Gandhinagar East Godavari Andhra Pradesh 533004";

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mb: 3.5 }}>
    <Typography component="h2" sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" }, fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ color: "text.secondary", lineHeight: 1.75 }}>{children}</Box>
  </Box>
);

const P = ({ children }) => (
  <Typography component="p" sx={{ mb: 1.25, lineHeight: 1.75 }}>
    {children}
  </Typography>
);

export default function AccountDeletion() {
  const [accountType, setAccountType] = useState("");
  const [campusCode, setCampusCode] = useState("");
  const [registeredContact, setRegisteredContact] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    document.title = "Account Deletion | UniFLOO";
  }, []);

  const emailBody = useMemo(
    () => [
      "I would like to request deletion of my UniFLOO account and eligible associated personal data.",
      "",
      `Account type: ${accountType || "Not provided"}`,
      `Campus code: ${campusCode.trim() || "Not provided"}`,
      `Registered email or phone: ${registeredContact.trim() || "Not provided"}`,
      `Additional information: ${notes.trim() || "None"}`,
      "",
      "Please contact me to verify my identity and confirm the next steps.",
      "I understand that certain institutional or legally required records may need to be retained.",
    ].join("\n"),
    [accountType, campusCode, registeredContact, notes]
  );

  const emailHref = `mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(
    "UniFLOO Account Deletion Request"
  )}&body=${encodeURIComponent(emailBody)}`;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, bgcolor: "background.paper" }}>
            <Typography component="h1" sx={{ fontSize: { xs: "1.8rem", md: "2.35rem" }, fontWeight: 800 }}>
              UniFLOO Account Deletion
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary" }}>Last updated: {LAST_UPDATED}</Typography>
          </Box>
          <Divider />
          <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, bgcolor: "background.paper" }}>
            <Section title="1. Request deletion of your account">
              <P>
                UniFLOO, operated by {OPERATOR}, provides a way to request deletion of an account
                and eligible associated personal information. You can make a request from this
                webpage even if you no longer have the mobile app installed.
              </P>
              <P>
                To request deletion, use the email request form below or email {PRIVACY_EMAIL}.
                Sending the request starts a review; it does not immediately delete your account.
              </P>
            </Section>

            <Section title="2. How to request deletion">
              <P>
                <strong>Alumni:</strong> In the UniFLOO mobile app, open Alumni Dashboard →
                Account Security → Request Account Deletion, if available in your version of the app.
                Alternatively, submit an email request below.
              </P>
              <P>
                <strong>Students and employees:</strong> Submit an email request below or contact
                your campus administration. Institution-managed records may require campus review.
              </P>
            </Section>

            <Section title="3. Prepare an email deletion request">
              <Alert severity="info" sx={{ mb: 2 }}>
                This form opens your email application with a prepared request. It does not submit
                information to UniFLOO directly. You must send the email to complete your request.
              </Alert>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="deletion-account-type-label">Account type</InputLabel>
                  <Select
                    labelId="deletion-account-type-label"
                    value={accountType}
                    label="Account type"
                    onChange={(event) => setAccountType(event.target.value)}
                  >
                    <MenuItem value="Alumni">Alumni</MenuItem>
                    <MenuItem value="Student">Student</MenuItem>
                    <MenuItem value="Employee">Employee</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Campus code (if known)"
                  size="small"
                  value={campusCode}
                  onChange={(event) => setCampusCode(event.target.value)}
                  inputProps={{ maxLength: 60 }}
                  fullWidth
                />
                <TextField
                  label="Registered email or mobile number"
                  size="small"
                  value={registeredContact}
                  onChange={(event) => setRegisteredContact(event.target.value)}
                  inputProps={{ maxLength: 160 }}
                  helperText="Do not include passwords, OTPs, identity documents, or other sensitive information."
                  fullWidth
                />
                <TextField
                  label="Additional information (optional)"
                  size="small"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  inputProps={{ maxLength: 500 }}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <Button
                  component="a"
                  href={emailHref}
                  variant="contained"
                  sx={{ alignSelf: "flex-start", bgcolor: "#ed7d31", "&:hover": { bgcolor: "#d66a1e" }, textTransform: "none", fontWeight: 700 }}
                >
                  Open Email to Request Deletion
                </Button>
                <Typography variant="body2" color="text.secondary">
                  If the button does not open your email app, send your request to{" "}
                  <Link href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</Link>.
                </Typography>
              </Stack>
            </Section>

            <Section title="4. Verification and processing">
              <P>
                We may ask you to verify ownership of the account before taking action. Where a
                campus controls the account or institutional records, the request may be referred
                to that institution for review. We will explain the next steps and any applicable
                limitations when responding to your request.
              </P>
            </Section>

            <Section title="5. Information deleted and information retained">
              <P>
                Depending on the account and services used, eligible information may include
                profile details, authentication methods, active sessions, profile photographs,
                and other account-associated data that is no longer required.
              </P>
              <P>
                Some academic, financial, transaction, audit, security, or institutional records
                may need to be retained for applicable legal, contractual, or institutional
                recordkeeping purposes. Retention periods vary by record type and institution;
                we will explain applicable retention when processing your request.
              </P>
              <P>
                Account deletion is not necessarily the same as deleting an institution's
                historical academic records. You may contact your institution about records it controls.
              </P>
            </Section>

            <Section title="6. Contact">
              <P>
                Operator: {OPERATOR}<br />
                Email: <Link href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</Link><br />
                Address: {BUSINESS_ADDRESS}
              </P>
              <P>
                For additional information, see our{" "}
                <Link href="/privacy" underline="hover">Privacy Policy</Link>.
              </P>
            </Section>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary">
              <Link href="/" underline="hover">Return to UniFLOO</Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
